/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { accessAPI, Boton, downloadFile, Sidebar, Titulo } from "../../shared";

// El asistente puede tardar bastante más que un request normal (lee documentos
// normativos antes de responder), así que este endpoint usa su propio timeout.
const TIMEOUT_ASISTENTE = 180000;

// El asistente se abre desde varios lugares (botón flotante, accesos de otras
// pantallas): todos disparan este evento y el panel, que está montado una
// sola vez en el Router, lo escucha.
const EVENTO_ABRIR = "portal:abrir-asistente";
export function abrirAsistente() {
  window.dispatchEvent(new Event(EVENTO_ABRIR));
}

const SUGERENCIAS = ["¿Qué tengo pendiente?", "¿Qué certificados vencen este año?"];

function BotonDescarga({ textoLink, endpoint, nombreArchivo, toast }) {
  const [descargando, setDescargando] = useState(false);

  return (
    <span
      css={{
        // inline-block y no block: así el texto que sigue al botón (un punto,
        // una coma) queda en el mismo renglón en vez de caer solo al siguiente.
        display: "inline-block",
        margin: "2px 0",
        verticalAlign: "middle",
        "& .p-button": {
          padding: "0.3em 0.6em",
          fontSize: "inherit",
        },
      }}
    >
      <Boton
        transparente
        loader={descargando}
        callback={() => {
          setDescargando(true);
          downloadFile(
            endpoint,
            nombreArchivo,
            () => setDescargando(false),
            () => {
              setDescargando(false);
              toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "No se pudo descargar el archivo",
                sticky: true,
              });
            }
          );
        }}
      >
        {textoLink}
      </Boton>
    </span>
  );
}

export default function Asistente() {
  const [pregunta, setPregunta] = useState("");
  const [visible, setVisible] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensajes, setMensajes] = useState([]);

  const toast = useRef(null);
  const conversacionRef = useRef(null);
  // Identifica la conversación actual: si el usuario la cierra mientras una
  // respuesta está en camino, la respuesta vieja no debe aparecer en la nueva.
  const conversacionId = useRef(0);

  useEffect(() => {
    const abrir = () => setVisible(true);
    window.addEventListener(EVENTO_ABRIR, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR, abrir);
  }, []);

  // Mantiene la conversación scrolleada al último mensaje
  useEffect(() => {
    if (conversacionRef.current) {
      conversacionRef.current.scrollTop = conversacionRef.current.scrollHeight;
    }
  }, [mensajes, cargando]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pregunta.trim() && !cargando) {
      const preguntaEnviada = pregunta.trim();

      // El historial son los mensajes previos a esta pregunta: la API los
      // manda a Claude como turnos de la conversación.
      const historial = mensajes;
      const idAlEnviar = conversacionId.current;
      setMensajes([...mensajes, { role: "user", content: preguntaEnviada }]);
      setPregunta("");
      setCargando(true);

      accessAPI(
        "POST",
        "cliente/juno",
        { prompt: preguntaEnviada, historial },
        (respuesta) => {
          if (conversacionId.current !== idAlEnviar) return;
          setMensajes((prev) => [
            ...prev,
            { role: "assistant", content: respuesta.respuesta || "" },
          ]);
          setCargando(false);
        },
        (error) => {
          if (conversacionId.current !== idAlEnviar) return;
          setCargando(false);
          if (toast.current) {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                error[0]?.msg ||
                "Ocurrió un error al procesar tu pregunta. Intenta nuevamente.",
              sticky: true,
            });
          }
        },
        null,
        null,
        { timeout: TIMEOUT_ASISTENTE }
      );
    }
  };

  function renderTextoConLinks(texto) {
    // Parsea links de descarga [texto](download:endpoint:nombre) y navegación [texto](link:/ruta)
    const partes = texto.split(/(\[[^\]]+\]\((?:download|link):[^)]+\))/g);
    return partes.map((parte, i) => {
      const matchDownload = parte.match(/\[([^\]]+)\]\(download:(.+):([^:]+)\)/);
      if (matchDownload) {
        const [, textoLink, endpoint, nombreArchivo] = matchDownload;
        return (
          <BotonDescarga
            key={i}
            textoLink={textoLink}
            endpoint={endpoint}
            nombreArchivo={nombreArchivo}
            toast={toast}
          />
        );
      }
      const matchLink = parte.match(/\[([^\]]+)\]\(link:([^)]+)\)/);
      if (matchLink) {
        const [, textoLink, ruta] = matchLink;
        return (
          <span
            key={i}
            css={{
              display: "inline-block",
              margin: "2px 0",
              verticalAlign: "middle",
              "& .p-button": {
                padding: "0.3em 0.6em",
                fontSize: "inherit",
              },
            }}
          >
            <Boton
              transparente
              callback={() => window.open(ruta, "_blank")}
            >
              {textoLink}
            </Boton>
          </span>
        );
      }
      // Un punto pegado después de un botón queda flotando a media altura del
      // botón, así que no se muestra: el botón ya cierra la frase visualmente.
      let textoPlano = parte;
      if (
        i > 0 &&
        /^\[[^\]]+\]\((?:download|link):[^)]+\)$/.test(partes[i - 1])
      ) {
        textoPlano = textoPlano.replace(/^\./, "");
      }
      return <span key={i}>{renderNegritas(textoPlano)}</span>;
    });
  }

  function renderNegritas(texto) {
    const partes = texto.split(/(\*\*[^*]+\*\*)/g);
    return partes.map((parte, i) => {
      const match = parte.match(/^\*\*(.+)\*\*$/);
      if (match) {
        return <strong key={i}>{match[1]}</strong>;
      }
      return parte;
    });
  }

  // El asistente arma tablas en markdown; acá se dibujan como tablas de verdad
  function esFilaDeTabla(linea) {
    return /^\s*\|.*\|\s*$/.test(linea);
  }

  function parsearFilaDeTabla(linea) {
    return linea.trim().slice(1, -1).split("|").map((celda) => celda.trim());
  }

  function esSeparadorDeTabla(celdas) {
    return celdas.length > 0 && celdas.every((c) => /^:?-{2,}:?$/.test(c));
  }

  function renderTabla(lineas, key) {
    const filas = lineas.map(parsearFilaDeTabla);
    const tieneHeader = filas.length > 1 && esSeparadorDeTabla(filas[1]);
    const header = tieneHeader ? filas[0] : null;
    const cuerpo = (tieneHeader ? filas.slice(2) : filas).filter(
      (f) => !esSeparadorDeTabla(f)
    );
    return (
      <div key={key} css={{ overflowX: "auto", margin: "6px 0" }}>
        <table
          css={{
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid rgba(0, 0, 0, 0.25)",
              padding: "4px 10px",
              textAlign: "left",
            },
            "& th": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          {header && (
            <thead>
              <tr>
                {header.map((celda, i) => (
                  <th key={i}>{renderTextoConLinks(celda)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {cuerpo.map((fila, i) => (
              <tr key={i}>
                {fila.map((celda, j) => (
                  <td key={j}>{renderTextoConLinks(celda)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Separa el contenido en bloques: las líneas consecutivas que son filas de
  // tabla se agrupan y renderizan como tabla; el resto, línea por línea.
  function renderContenido(texto) {
    const bloques = [];
    let tabla = [];
    texto.split("\n").forEach((linea) => {
      if (esFilaDeTabla(linea)) {
        tabla.push(linea);
      } else {
        if (tabla.length > 0) {
          bloques.push({ tipo: "tabla", lineas: tabla });
          tabla = [];
        }
        bloques.push({ tipo: "linea", linea });
      }
    });
    if (tabla.length > 0) bloques.push({ tipo: "tabla", lineas: tabla });

    return bloques.map((bloque, i) =>
      bloque.tipo === "tabla" ? (
        renderTabla(bloque.lineas, i)
      ) : (
        <span key={i}>
          {renderTextoConLinks(bloque.linea)}
          <br />
        </span>
      )
    );
  }


  function renderMensaje(mensaje, index) {
    if (mensaje.role === "user") {
      return (
        <div key={index} css={estiloBurbuja(true)}>
          {mensaje.content}
        </div>
      );
    }
    return (
      <div key={index} css={estiloBurbuja(false)}>
        {renderContenido(mensaje.content)}
      </div>
    );
  }

  // Reiniciar arranca una conversación nueva
  const reiniciar = () => {
    setMensajes([]);
    setCargando(false);
    setPregunta("");
    conversacionId.current += 1;
  };

  const preguntar = (texto) => setPregunta(texto);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={visible}
        onHide={() => setVisible(false)}
        position="right"
        className="w-full"
        blockScroll
        css={{
          // Un poco más ancho que los paneles de formulario (30rem): las
          // respuestas traen tablas y párrafos largos. En mobile, todo el ancho.
          "@media (min-width: 768px)": { width: "38rem !important" },
          // Conversación que ocupa el alto y formulario fijo abajo
          "& .p-sidebar-content": { display: "flex", flexDirection: "column", height: "100%" }
        }}
      >
        <Titulo
          botones={
            <Boton icono disabled={!mensajes.length && !cargando} callback={reiniciar}>
              <i className="pi pi-refresh"></i>
            </Boton>
          }
        >
          Asistente
        </Titulo>
        <p css={{ margin: "0 0 1em", fontSize: "0.85em", color: "var(--grisTextos)" }}>
          Interpreto lenguaje natural y respondo preguntas sobre tus certificados y los esquemas de
          certificación nacionales. Las respuestas pueden no ser precisas: puedes verificarlas con
          nuestro personal siempre que desees.
        </p>
        <div
          ref={conversacionRef}
          css={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.75em", paddingBottom: "1em" }}
        >
          {mensajes.length === 0 && (
            <div css={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5em" }}>
              <span css={{ fontWeight: 600, color: "var(--grisTextos)" }}>Preguntas sugeridas</span>
              {SUGERENCIAS.map((s) => (
                <Boton key={s} chico texto callback={() => preguntar(s)}>
                  {s}
                </Boton>
              ))}
            </div>
          )}
          {mensajes.map((mensaje, index) => renderMensaje(mensaje, index))}
          {cargando && (
            <div css={[estiloBurbuja(false), { display: "flex", alignItems: "center", gap: "0.5em" }]}>
              <i className="pi pi-spin pi-spinner" />
              <span>Procesando tu pregunta…</span>
            </div>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          css={{ display: "flex", gap: "0.5em", alignItems: "center", paddingTop: "1em", borderTop: "1px solid #e5e5e5" }}
        >
          <InputText
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            aria-label="Tu pregunta"
            css={{ flex: 1 }}
            autoFocus
          />
          <Boton texto disabled={!pregunta.trim() || cargando} loader={cargando} callback={handleSubmit}>
            Enviar
          </Boton>
        </form>
      </Sidebar>
    </>
  );
}

// Globito de un mensaje: los del usuario a la derecha con el color del
// organismo; los del asistente a la izquierda, en gris claro
function estiloBurbuja(delUsuario) {
  return {
    maxWidth: "88%",
    padding: "0.6em 0.85em",
    lineHeight: 1.45,
    wordBreak: "break-word",
    alignSelf: delUsuario ? "flex-end" : "flex-start",
    background: delUsuario ? "var(--principal)" : "#f4f4f4",
    color: delUsuario ? "white" : "rgba(44, 44, 44, 1)",
    borderRadius: delUsuario ? "14px 14px 4px 14px" : "14px 14px 14px 4px"
  };
}
